import { noticeError } from '@ratehub/base-ui';
import {
    fetchAllRates,
    getMonthlyPaymentForScenario,
    MORTGAGE_EXTENDED_RATE_TYPES,
    SCENARIO_TYPES,
} from '@ratehub/mtg-common';

import {
    ALL_MORTGAGE_SCENARIO_DEFAULT_PARAMS,
} from '../definitions/MortgageTerms';

/**
 * Fetches mortgage rates for the ProductTableMortgages depending on term
 * @param {object} params
 * @param {string} params.type fixed or variable mortgage
 * @param {string} params.term length of mortgage
 * @param {string} params.scenario scenario of mortgage (purchase, renewal, etc)
 * @param {string} params.province province code
 * @param {string|object} params.city string = city slug, object = { name, province.code }
 * @param {string} params.language language code
 */
async function fetchProductTableMortgagesRates(values) {
    const params = getParamsForScenario(values);

    try {
        const fetchedRates = await fetchAllRates(params.scenario, params);

        // Since we're debouncing fetchRates, we want this monthly payment calculation to occur
        //  when the rates change. Otherwise this would occur with the onChange and the
        //  user would see the monthly payment change before the rates get updated.
        //! Need to use for loop instead of .map because we have to preserve array properties.
        for (const rate of fetchedRates) {
            rate.monthlyPayment = getMonthlyPayment(rate.value, values);
        }

        return fetchedRates;
    } catch (error) {
        noticeError(error, {
            message: '[ProductTableMortgages] Unable to fetch rates',
        });

        // Re-throw so the FE can handle error messaging
        throw error;
    }
}

function getParamsForScenario(values) {
    const scenario = values.scenario ?? SCENARIO_TYPES.PURCHASE;
    const city = values.scenario === SCENARIO_TYPES.HELOC && values.helocCity
        ? values.helocCity
        : values.city;

    const params = {
        ...ALL_MORTGAGE_SCENARIO_DEFAULT_PARAMS[scenario],
        scenario,

        city: city?.name    // some users pass a city object
            ?? city         // others pass only the name as a string
            ?? ALL_MORTGAGE_SCENARIO_DEFAULT_PARAMS[scenario].city,

        province: values?.province      // directly as a string
            ?? city?.province?.code     // within a city object
            ?? ALL_MORTGAGE_SCENARIO_DEFAULT_PARAMS[scenario].province,

        language: values.language,

        ...(scenario !== SCENARIO_TYPES.HELOC && {
            ...(values.term && { term: values.term }),
            ...(values.occupancy && { intendedUse: values.occupancy }),
            ...(values.type && { type: values.type }),
            ...(values.type && values.type === MORTGAGE_EXTENDED_RATE_TYPES.FIXED_OPEN && {
                type: MORTGAGE_EXTENDED_RATE_TYPES.FIXED,
                isOpen: 1,
            }),
            ...(values.type && values.type === MORTGAGE_EXTENDED_RATE_TYPES.VARIABLE_OPEN && {
                type: MORTGAGE_EXTENDED_RATE_TYPES.VARIABLE,
                isOpen: 1,
            }),
            ...(values.type && values.type === MORTGAGE_EXTENDED_RATE_TYPES.CASHBACK && {
                type: MORTGAGE_EXTENDED_RATE_TYPES.FIXED,
                isCashBack: 1,
            }),
        }),
    };

    switch (values.scenario) {
        case SCENARIO_TYPES.PURCHASE:
            return {
                ...params,
                ...(values.homePrice && { homePrice: values.homePrice }),
                ...(values.downPaymentPercent && { downPaymentPercent: values.downPaymentPercent / 100 }),
                ...(values.purchaseRefinanceAmortization && { amortization: values.purchaseRefinanceAmortization }),
            };
        case SCENARIO_TYPES.RENEWAL:
            return {
                ...params,
                ...(values.renewAmortization && { amortization: values.renewAmortization }),
                ...(values.isCMHC != null && { isInsured: values.isCMHC }),
            };
        case SCENARIO_TYPES.REFINANCE:
            return {
                ...params,
                ...(values.purchaseRefinanceAmortization && { amortization: values.purchaseRefinanceAmortization }),
            };
        // HELOC doesn't have any additions
        case SCENARIO_TYPES.HELOC:
        default:
            return params;
    }
}

function getMonthlyPayment(rateValue, values) {
    const params = {
        ...values,
        ...(values.scenario !== SCENARIO_TYPES.HELOC && {
            amortization: values.scenario === SCENARIO_TYPES.RENEWAL
                ? values.renewAmortization
                : values.purchaseRefinanceAmortization,
        }),
        ...(values.scenario !== SCENARIO_TYPES.HELOC && values.scenario !== SCENARIO_TYPES.PURCHASE && {
            currentMortgageBalance: values.scenario === SCENARIO_TYPES.RENEWAL
                ? values.renewMortgageBalance
                : values.refinanceMortgageBalance,
        }),
    };

    return getMonthlyPaymentForScenario(rateValue, params);
}

export default fetchProductTableMortgagesRates;
